/**
 * Поисковый индекс для рекурсивных (древовидных) структур.
 *
 * Базовые функции (tokenizeSearch, makeVariants, matchesTokens, buildHighlightSpans)
 * реэкспортируются из общего модуля searchHighlight.ts.
 * Здесь — только логика, специфичная для дерева: построение индекса, вычисление
 * видимых/развёрнутых узлов при поиске.
 */
import {
  tokenizeSearch,
  makeVariants,
  matchesTokens,
  buildHighlightSpans,
  HIGHLIGHT_STYLE,
} from '@/components/common/basic/search-highlight/searchHighlight';

export type { HighlightSpan } from '@/components/common/basic/search-highlight/searchHighlight';
export { tokenizeSearch, makeVariants, matchesTokens, buildHighlightSpans, HIGHLIGHT_STYLE };

/**
 * Плоские Map-справочники по всему дереву, построенные один раз при изменении данных.
 * Позволяют за O(1) получить узел, текст, нормализованные варианты, родителя и детей по ключу.
 */
export interface RecursiveSearchIndex<T = any> {
  nodeByKey: Map<string, T>;
  textByKey: Map<string, string>;
  /** Раскладочные варианты имени (результат makeVariants) для каждого узла. */
  normalizedByKey: Map<string, string[]>;
  parentByKey: Map<string, string | null>;
  childrenByKey: Map<string, string[]>;
  rootKeys: string[];
}

/**
 * Результат поиска по дереву.
 * - matchedKeys — узлы, напрямую совпавшие со всеми токенами.
 * - visibleKeys — узлы, которые должны быть видны (matched + их предки + потомки matched).
 * - autoExpandedKeys — узлы, которые надо автоматически развернуть (имеют детей на пути к matched).
 */
export interface RecursiveSearchResult {
  tokens: string[];
  matchedKeys: Set<string>;
  visibleKeys: Set<string>;
  autoExpandedKeys: Set<string>;
  searchActive: boolean;
}

/**
 * Строит поисковый индекс по дереву.
 * Обходит всё дерево рекурсивно, заполняет Map-справочники.
 *
 * @param params.roots — корневые узлы дерева.
 * @param params.getChildren — функция получения дочерних узлов.
 * @param params.getKey — функция генерации уникального ключа узла.
 * @param params.getText — функция получения текста для поиска (обычно Name).
 */
export function buildRecursiveSearchIndex<T>(params: {
  roots: T[];
  getChildren: (node: T) => T[];
  getKey: (node: T, parentKey: string | null, index: number) => string;
  getText: (node: T) => string;
}): RecursiveSearchIndex<T> {
  const nodeByKey = new Map<string, T>();
  const textByKey = new Map<string, string>();
  const normalizedByKey = new Map<string, string[]>();
  const parentByKey = new Map<string, string | null>();
  const childrenByKey = new Map<string, string[]>();
  const rootKeys: string[] = [];

  const visit = (nodes: T[], parentKey: string | null) => {
    nodes.forEach((node, index) => {
      const key = params.getKey(node, parentKey, index);
      const text = params.getText(node) || '';

      nodeByKey.set(key, node);
      textByKey.set(key, text);
      normalizedByKey.set(key, makeVariants(text));
      parentByKey.set(key, parentKey);

      if (parentKey == null) rootKeys.push(key);

      const children = params.getChildren(node) || [];
      const childKeys: string[] = [];
      children.forEach((child, childIndex) => {
        const childKey = params.getKey(child, key, childIndex);
        childKeys.push(childKey);
      });
      childrenByKey.set(key, childKeys);

      visit(children, key);
    });
  };

  visit(params.roots || [], null);

  return {
    nodeByKey,
    textByKey,
    normalizedByKey,
    parentByKey,
    childrenByKey,
    rootKeys,
  };
}

/** Добавляет в set все предки узла (включая сам узел), поднимаясь по parentByKey до корня. */
function addAncestors(key: string, parentByKey: Map<string, string | null>, into: Set<string>) {
  let cursor: string | null | undefined = key;
  while (cursor != null) {
    into.add(cursor);
    cursor = parentByKey.get(cursor) ?? null;
  }
}

/** Добавляет в set сам узел и всех его потомков рекурсивно. */
function addDescendants(key: string, childrenByKey: Map<string, string[]>, into: Set<string>) {
  into.add(key);
  const children = childrenByKey.get(key) || [];
  children.forEach((childKey) => addDescendants(childKey, childrenByKey, into));
}

/**
 * Выполняет поиск по предварительно построенному индексу.
 *
 * Алгоритм:
 * 1. Токенизирует строку фильтра (tokenizeSearch).
 * 2. Для каждого узла проверяет matchesTokens — все ли токены найдены.
 * 3. Для каждого совпавшего узла:
 *    - все его предки добавляются в visibleKeys (чтобы путь до корня был виден);
 *    - все его потомки добавляются в visibleKeys (чтобы при раскрытии видеть детей);
 *    - все предки с детьми добавляются в autoExpandedKeys (чтобы дерево раскрылось до совпадения).
 *
 * @returns RecursiveSearchResult с наборами ключей для управления видимостью и раскрытием.
 */
export function searchInRecursiveIndex(index: RecursiveSearchIndex<any>, filter: string): RecursiveSearchResult {
  const tokens = tokenizeSearch(filter);
  const searchActive = tokens.length > 0;

  if (!searchActive) {
    return {
      tokens: [],
      matchedKeys: new Set(index.nodeByKey.keys()),
      visibleKeys: new Set(index.nodeByKey.keys()),
      autoExpandedKeys: new Set<string>(),
      searchActive: false,
    };
  }

  const tokenVariantsCache = tokens.map((t) => makeVariants(t));

  const matchedKeys = new Set<string>();
  index.normalizedByKey.forEach((variants, key) => {
    if (matchesTokens(variants, tokenVariantsCache)) matchedKeys.add(key);
  });

  const visibleKeys = new Set<string>();
  const autoExpandedKeys = new Set<string>();

  matchedKeys.forEach((key) => {
    addAncestors(key, index.parentByKey, visibleKeys);
    addDescendants(key, index.childrenByKey, visibleKeys);

    let cursor: string | null = key;
    while (cursor != null) {
      const children = index.childrenByKey.get(cursor) || [];
      if (children.length) autoExpandedKeys.add(cursor);
      cursor = index.parentByKey.get(cursor) ?? null;
    }
  });

  return {
    tokens,
    matchedKeys,
    visibleKeys,
    autoExpandedKeys,
    searchActive,
  };
}
